import React from "react";
import { graphql, useFragment } from "react-relay";

import {
	faAlignLeft,
	faArrowsV,
	faCalendarCheck,
	faCalendarDay,
	faClock,
	faEllipsisH,
	faGlobeAmericas,
	faLongArrowRight,
	faRepeat,
	faVideo,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faExclamationTriangle } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { isSameWeek, isThisYear, set } from "date-fns";
import { useInView } from "framer-motion";
import { noop } from "lodash";

import { GlobalKeyboardListener } from "~/common";
import { useID } from "~/lib/hooks";
import useToggle from "~/lib/hooks/useToggle";
import { confirm } from "~/lib/prompts";
import recaptcha from "~/lib/recaptcha";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { SUPPORTED_CALL_PROVIDERS_OPTIONS } from "~/meetingRecorder/MeetingRecordingBanner";
import { RecordIconSwitch } from "~/meetingRecorder/RecordIcon";
import { Button, Checkbox, toast, Tooltip } from "~/ui";
import { scrollToImaginaryEvent } from "~/ui/calendar/utils/scrolling";
import { Input, TextArea } from "~/ui/legacyFellowIndex";
import LoadingSpinner from "~/ui/LoadingSpinner";
import Menu from "~/ui/menu";
import type { SelectorOption, SingleValue } from "~/ui/selectors/Selector";

import {
	type Calendar,
	type Event,
	type MeetingCostSettings,
	type MeetingGuidelinesSettings,
	MeetWithContext,
} from "../CalendarPage";
import { CONFERENCE_PROVIDERS, type ConferenceOption, getProviderFromPlatform } from "../hooks/useConferencingOptions";
import useConferencingOptions from "../hooks/useConferencingOptions";
import type { DateTimeAction } from "../hooks/useDateTimeState";
import {
	type Participant,
	type ParticipantsReducerAction,
	useParticipantsState,
} from "../hooks/useParticipantsOptions";
import usePreferredTimezone from "../hooks/usePreferredTimezone";
import type { RepeatOption } from "../hooks/useRecurrenceOptions";
import useSaveEventMutation, { TITLE_MAX_LENGTH } from "../hooks/useSaveEventMutation";
import { dateToTimeValue, type TimeValue } from "../hooks/useTimeOptions";
import { FiniteRecurringMeetings, NoMeetingDay, ShortNotice, SpeedyMeetings } from "../MeetingGuidelinesPrompts";
import { SidePanelNavContext } from "../SidePanel";
import { OutOfCreditsWarning } from "../ViewEvent/RecordingRow";
import { ReactComponent as ColorSquareClipped } from "./color-square-clipped.svg";
import { ReactComponent as ColorSquareSVG } from "./color-square.svg";
import ColorPicker, { type EventColor } from "./ColorPicker";
import { conferenceOptionLabelCreator } from "./ConferenceSelector";
import DateInput from "./DateInput";
import { EventFormContext } from "./EventFormContext";
import Selector from "./IndicatorlessSelector";
import ParticipantsSelector from "./ParticipantsSelector";
import recurrenceChoiceModal from "./recurrenceChoiceModal";
import { TimeSelector } from "./TimeSelector";

import type { EventForm_viewer$key } from "~/__generated__/EventForm_viewer.graphql";
import type { EventFormDetailQuery } from "~/__generated__/EventFormDetailQuery.graphql";
import type { EventEditType } from "~/__generated__/MeetingsPanelAttendanceMutation.graphql";

import guidelinesPromptStyles from "../MeetingGuidelinesPrompts/MeetingGuidelines.module.css";
import styles from "./EventForm.module.css";

export const eventFormDetails = graphql`
	query EventFormDetailQuery($guid: String) {
		event: eventByGuid(guid: $guid) {
			... on Event {
				id
				title
				description
				guid
				...RecordIcon_event @relay(mask: false)
				mainConferenceCall {
					provider
					url
					isRemovable
				}
				start
				end
				isAllDay
				recurrenceRule
				providerEventColor: color
				fellowColor
				stream {
					schedulingMeetingAssistantAutomationIsActive
				}
				attendees {
					email
					fullName
					image
					responseStatus
					isOptional
					user {
						id
						isConfigured
						isGuest
						isExternal
						firstName
					}
				}
				calendar {
					id
					source
					sourcePrimary
					name
					color
					syncEnabled
					lastSyncDatetime
					hadErrorOnSync
				}
			}
		}
	}
`;

export interface CalendarOption extends SelectorOption {
	color: Calendar["color"];
}

declare module "csstype" {
	interface Properties {
		"--color"?: string;
	}
}

function ColorSquare({ color, clipped }: { color: string; clipped?: boolean }) {
	return clipped ? (
		<ColorSquareClipped className={styles.calendarColorSquare} style={{ "--color": color }} />
	) : (
		<ColorSquareSVG className={styles.calendarColorSquare} style={{ "--color": color }} />
	);
}

interface CalendarSelectorValueDisplayProps {
	fellowColor: EventColor;
	providerEventColor: string | null;
	calendarColor: string;
	selectedCalendar: Calendar;
}

function CalendarSelectorValueDisplay({
	fellowColor,
	providerEventColor,
	calendarColor,
	selectedCalendar,
}: CalendarSelectorValueDisplayProps) {
	const calendarName = React.useMemo(
		() =>
			selectedCalendar.source === "internal"
				? "Fellow Calendar"
				: selectedCalendar.sourcePrimary
					? "Primary Calendar"
					: (selectedCalendar.name ?? "(no name)"),
		[selectedCalendar],
	);

	if (fellowColor === "CALENDAR" || (fellowColor === "PROVIDER" && !providerEventColor)) {
		return (
			<>
				<span className={styles.calendarSelectorColors}>
					<ColorSquare color={calendarColor} />
				</span>
				{calendarName}
			</>
		);
	}

	return (
		<>
			<span className={styles.calendarSelectorColors}>
				<ColorSquare color={calendarColor} clipped />
				<ColorSquare
					color={
						fellowColor === "PROVIDER" && providerEventColor
							? providerEventColor
							: `var(--event-${fellowColor.toLowerCase()})`
					}
				/>
			</span>
			{calendarName}
		</>
	);
}

function CalendarSelectorOption({
	calendar,
	selected,
	onSelect,
}: {
	calendar: Calendar;
	selected: boolean;
	onSelect: () => void;
}) {
	const label =
		calendar.source === "internal"
			? "Fellow Calendar"
			: calendar.sourcePrimary
				? "Primary Calendar"
				: (calendar.name ?? "(no name)");
	const color = calendar.color ?? "var(--vividRoyal)";

	return (
		<Menu.RadioItem
			value={calendar.id}
			textValue={label}
			className={cx(styles.calendarSelectorOption, { [styles.selected]: selected })}
			onSelect={onSelect}
		>
			<span className={styles.calendarSelectorColors}>
				<ColorSquare color={color} />
			</span>
			{label}
		</Menu.RadioItem>
	);
}

export type EventFormResultEvent = Pick<Event, "start" | "end" | "title" | "isAllDay"> & {
	calendarId: string;
	// If eventGuid is null, the event previously existed and was edited
	eventGuid: string | null;
};

export type EventFormResult =
	| { type: "cancelled" | "failed" }
	| { type: "deleted"; event: { eventGuid: string } }
	| { type: "succeeded"; event: EventFormResultEvent };

interface EventFormProps {
	eventAdditional: EventFormDetailQuery["response"]["event"];
	setHasEdits: () => void;
	onFormFinish: (result: EventFormResult) => void;
	viewer: EventForm_viewer$key;
	meetingGuidelinesSettings: MeetingGuidelinesSettings;
	meetingCostSettings: MeetingCostSettings;
	hasAccessToSchedulingMeetingAssistant: boolean;
}

type MeetingGuidelinesRenderedType = {
	noMeetingDay: boolean | undefined;
	shortNotice: boolean | undefined;
	finiteRecurringMeetings: boolean | undefined;
	meetingAttendees: boolean | undefined;
};

export default function EventForm({
	onFormFinish,
	viewer: viewerKey,
	eventAdditional,
	setHasEdits,
	meetingGuidelinesSettings,
	meetingCostSettings,
	hasAccessToSchedulingMeetingAssistant,
}: EventFormProps) {
	const [isTitleInputDirty, setIsTitleInputDirty] = React.useState(false);
	const viewer = useFragment(
		graphql`
			fragment EventForm_viewer on Viewer {
				remainingMeetingRecordingCredits

				account {
					name
				}

				user {
					id
					firstName
				}

				autoRecordSettings {
					enabled
					meetingOwnershipFilter
				}

				...EventsCalendars @relay(mask: false)
				...useConferencingOptions_viewer
				...useParticipantsOptions_viewer
				...usePreferredTimezone_viewer
				...RecordingRowOutOfCreditsWarning_viewer
			}
		`,
		viewerKey,
	);
	const { user } = viewer;
	const calendars = React.useMemo(
		() => viewer.calendars?.edges.map(cal => cal?.node).filter((cal): cal is Calendar => Boolean(cal)) ?? [],
		[viewer.calendars],
	);

	const onCancel = React.useCallback(() => onFormFinish({ type: "cancelled" }), [onFormFinish]);

	const saveEvent = useSaveEventMutation();
	const isEdit = !!eventAdditional;

	// This is used to limit the number of prompts rendered at once and to determine whether to show the "Save anyway" button.
	const [meetingGuidelinesRendered, setMeetingGuidelinesRendered] = React.useState<MeetingGuidelinesRenderedType>({
		noMeetingDay: false,
		shortNotice: false,
		finiteRecurringMeetings: false,
		meetingAttendees: false,
	});

	const setNoMeetingDayRendered = React.useCallback((value: boolean) => {
		setMeetingGuidelinesRendered(prev => ({ ...prev, noMeetingDay: value }));
	}, []);

	const setShortNoticeRendered = React.useCallback((value: boolean) => {
		setMeetingGuidelinesRendered(prev => ({ ...prev, shortNotice: value }));
	}, []);

	const setFiniteRecurringMeetingsRendered = React.useCallback((value: boolean) => {
		setMeetingGuidelinesRendered(prev => ({ ...prev, finiteRecurringMeetings: value }));
	}, []);

	const setMeetingAttendeesRendered = React.useCallback((value: boolean) => {
		setMeetingGuidelinesRendered(prev => ({ ...prev, meetingAttendees: value }));
	}, []);

	const shouldShowSaveAnywayPrompt = React.useMemo(() => {
		if (!meetingGuidelinesSettings?.promptWhenSavingEnabled) return false;

		if (meetingGuidelinesRendered.finiteRecurringMeetings) return true;

		if (meetingGuidelinesRendered.shortNotice) return true;

		if (meetingGuidelinesRendered.noMeetingDay) return true;

		if (meetingGuidelinesRendered.meetingAttendees) return true;

		return false;
	}, [
		meetingGuidelinesSettings?.promptWhenSavingEnabled,
		meetingGuidelinesRendered.finiteRecurringMeetings,
		meetingGuidelinesRendered.shortNotice,
		meetingGuidelinesRendered.noMeetingDay,
		meetingGuidelinesRendered.meetingAttendees,
	]);

	const autoRecordEnabled = React.useMemo(() => {
		return (
			(viewer.autoRecordSettings?.enabled && viewer.autoRecordSettings?.meetingOwnershipFilter !== "CUSTOM") ?? false
		);
	}, [viewer.autoRecordSettings]);

	const {
		eventTitle: title,
		setEventTitle: setTitle,
		timeState,
		changeTime: _changeTime,
		repeatState,
		customRrule,
		changeRecurrence,
		setRecurrenceFromRrule,
		repeatOptions,
	} = React.useContext(EventFormContext);
	const { systemToUserTime, userToSystemTime } = usePreferredTimezone(viewer);
	const { meetWithParticipants, setMeetWithParticipants, restorePreviousMeetWithParticipants } =
		React.useContext(MeetWithContext);
	const { week, setWeek } = React.useContext(SidePanelNavContext);

	const [hasRecurrenceChanges, setHasRecurrenceChanges] = React.useState(false);

	const now = Date.now();
	const ONE_MINUTE_IN_MILLISECONDS = 60 * 1000;
	const isMeetingStartingSoon = timeState.start.getTime() - 5 * ONE_MINUTE_IN_MILLISECONDS < now;

	React.useEffect(() => {
		if (eventAdditional?.recurrenceRule) {
			setRecurrenceFromRrule(eventAdditional.recurrenceRule);
			setHasRecurrenceChanges(false);
		}
	}, [eventAdditional?.recurrenceRule, setRecurrenceFromRrule]);

	// Setting the `autofocus` attribute on the first input breaks the entry animation for whatever reason. This is a workaround.
	const firstInputRef = React.useRef<HTMLInputElement>(null);
	React.useEffect(() => {
		const timeout = setTimeout(() => {
			if (firstInputRef.current && !isEdit) firstInputRef.current.focus();
		}, 300); // The animation takes 300ms

		return () => clearTimeout(timeout);
	}, [firstInputRef, isEdit]);

	const blurOnEscape = React.useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Escape") {
			e.preventDefault();
			e.currentTarget.blur();
		}
	}, []);

	const onTitleChange = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setTitle(e.target.value);
			setHasEdits();
			setIsTitleInputDirty(true);
		},
		[setTitle, setHasEdits],
	);

	const titleError = React.useMemo(
		() => (title.trim().length >= TITLE_MAX_LENGTH ? `Title must be fewer than ${TITLE_MAX_LENGTH} characters` : ""),
		[title],
	);

	const emptyTitleError = React.useMemo(
		() => (title.trim().length === 0 ? "Please give your event a title." : ""),
		[title],
	);

	const changeTime = React.useCallback(
		(params: DateTimeAction) => {
			_changeTime(params);
			setHasEdits();

			const newTime = params.type === "SET" ? params.newStart : params.newTime;
			if (!newTime) return;
			if (!isSameWeek(newTime, week)) setWeek(newTime);

			scrollToImaginaryEvent();
		},
		[_changeTime, setHasEdits, week, setWeek],
	);

	const changeStartTime = React.useCallback(
		(time: TimeValue) => {
			const newValue = set(timeState.start, { hours: time.hour, minutes: time.minute });
			changeTime({
				type: "SHIFT",
				newTime: newValue,
			});
		},
		[timeState.start, changeTime],
	);

	const changeStartDate = React.useCallback(
		(date: Date) => {
			const newValue = set(date, { hours: timeState.start.getHours(), minutes: timeState.start.getMinutes() });
			changeTime({
				type: "SHIFT",
				newTime: newValue,
			});
		},
		[timeState, changeTime],
	);

	const changeEndTime = React.useCallback(
		(time: TimeValue) => {
			const newValue = set(timeState.end, { hours: time.hour, minutes: time.minute });
			changeTime({
				type: "CHANGE_END",
				newTime: newValue,
			});
		},
		[timeState.end, changeTime],
	);

	const changeEndDate = React.useCallback(
		(date: Date) => {
			const newValue = set(date, { hours: timeState.end.getHours(), minutes: timeState.end.getMinutes() });
			changeTime({
				type: "CHANGE_END",
				newTime: newValue,
			});
		},
		[timeState, changeTime],
	);

	const toggleAllDay = React.useCallback(() => changeTime({ type: "TOGGLE_ALL_DAY" }), [changeTime]);

	const [isExtraOptionsOpen, toggleExtraOptions] = useToggle(false);

	const onChangeRecurrence = React.useCallback(
		(option: SingleValue<RepeatOption>) => {
			if (!option) return;
			changeRecurrence(option.repeat);
			setHasRecurrenceChanges(true);
			setHasEdits();
		},
		[changeRecurrence, setHasEdits],
	);

	const initParticipants = isEdit
		? (eventAdditional.attendees
				.filter(a => !a || !a.user || a.user.id !== user?.id)
				.map(a => ({
					...a,
					...(a && a.user ? a.user : {}),
					hasConfiguredUser: a?.user?.isConfigured,
				})) as Participant[])
		: meetWithParticipants;
	const [selectedParticipants, _changeParticipants] = useParticipantsState(initParticipants);
	const changeParticipants = React.useCallback(
		(val: ParticipantsReducerAction) => {
			_changeParticipants(val);
			setHasEdits();
		},
		[_changeParticipants, setHasEdits],
	);

	const [isCalendarMenuOpen, setCalendarMenuOpen] = React.useState(false);
	React.useEffect(() => {
		setMeetWithParticipants(selectedParticipants);
	}, [selectedParticipants, setMeetWithParticipants]);

	React.useEffect(() => {
		return () => {
			restorePreviousMeetWithParticipants();
		};
	}, [restorePreviousMeetWithParticipants]);

	const hasAtLeastOneRequiredParticipant = React.useMemo(
		() => selectedParticipants.some(p => !p.isOptional),
		[selectedParticipants],
	);

	React.useEffect(() => {
		if (isTitleInputDirty || isEdit || selectedParticipants.length !== 1) return;

		const otherName = selectedParticipants[0].user?.firstName || selectedParticipants[0].fullName;
		if (!otherName) return;

		setTitle(`${user?.firstName || "Me"} <> ${otherName}`);
	}, [isEdit, isTitleInputDirty, selectedParticipants, setTitle, user?.firstName]);

	const shouldRenderSpeedyMeetings = React.useMemo(() => {
		if (!meetingGuidelinesSettings?.breaksBetweenMeetingsEnabled || isEdit || !hasAtLeastOneRequiredParticipant) {
			return false;
		}

		return (
			!(meetingGuidelinesRendered.noMeetingDay && meetingGuidelinesRendered.shortNotice) &&
			!(meetingGuidelinesRendered.noMeetingDay && meetingGuidelinesRendered.finiteRecurringMeetings) &&
			!(meetingGuidelinesRendered.shortNotice && meetingGuidelinesRendered.finiteRecurringMeetings)
		);
	}, [
		hasAtLeastOneRequiredParticipant,
		isEdit,
		meetingGuidelinesRendered.finiteRecurringMeetings,
		meetingGuidelinesRendered.noMeetingDay,
		meetingGuidelinesRendered.shortNotice,
		meetingGuidelinesSettings?.breaksBetweenMeetingsEnabled,
	]);

	const googleCalendar = calendars.find(c => c.source === "google");
	const officeCalendar = calendars.find(c => c.source === "office365");
	const [selectedCalendar, setSelectedCalendar] = React.useState(
		eventAdditional?.calendar || googleCalendar || officeCalendar || calendars[0],
	);

	const changeCalendarFn = React.useCallback(
		(selectedCalendar: Calendar) => () => {
			setHasEdits();
			return setSelectedCalendar(selectedCalendar);
		},
		[setSelectedCalendar, setHasEdits],
	);

	const calendarColor = selectedCalendar.color ?? "var(--vividRoyal)";
	const [eventColor, setEventColor] = React.useState<EventColor>(() => {
		if (eventAdditional?.fellowColor) return eventAdditional?.fellowColor;
		return "PROVIDER";
	});
	const onChangeColor = React.useCallback(
		(color: EventColor) => {
			setHasEdits();
			setEventColor(color);
			setCalendarMenuOpen(false);
		},
		[setHasEdits],
	);

	const { conferenceOptions, conferenceProvider, setConferenceProvider } = useConferencingOptions(
		viewer,
		isEdit ? getProviderFromPlatform(eventAdditional.mainConferenceCall?.provider) : undefined,
	);
	const onChangeConferenceProvider = React.useCallback(
		(newOption: SingleValue<ConferenceOption>) => {
			setConferenceProvider(newOption?.value ?? "NONE");
			setHasEdits();
		},
		[setConferenceProvider, setHasEdits],
	);

	const [isDescriptionBoxVisible, toggleDescriptionBox] = useToggle(
		Boolean(isEdit) && Boolean(eventAdditional?.description),
	);
	const [eventDescription, setEventDescription] = React.useState(eventAdditional?.description || "");
	const onChangeEventDescription = React.useCallback(
		(e: React.ChangeEvent<HTMLTextAreaElement>) => {
			setEventDescription(e.target.value);
			setHasEdits();
		},
		[setHasEdits],
	);

	const [schedulingAutomationIsActive, setSchedulingAutomationIsActive] = React.useState(
		isEdit ? (eventAdditional?.stream?.schedulingMeetingAssistantAutomationIsActive ?? false) : autoRecordEnabled,
	);

	const shouldShowAutoRecordingToggle =
		hasAccessToSchedulingMeetingAssistant &&
		!isMeetingStartingSoon &&
		SUPPORTED_CALL_PROVIDERS_OPTIONS.includes(conferenceProvider);

	const [formLoading, setFormLoading] = React.useState(false);
	const [formPreLoading, setFormPreLoading] = React.useState(false);

	const submitForm = React.useCallback(async () => {
		setFormPreLoading(true);

		const captchaToken = await recaptcha("calendar_page_event_form");

		const sendNotifications =
			selectedCalendar.source === "google" &&
			selectedParticipants.length > 0 &&
			(await confirm("Do you wish to send email invitations to the participants?", {
				yesText: "Send",
				noText: "Don't send",
				simple: true,
				title: "Notify participants",
			}));

		// Modal was dismissed
		if (sendNotifications === null) {
			setFormPreLoading(false);
			return;
		}

		const [attendees, optionalAttendees]: [string[], string[]] = selectedParticipants.reduce(
			([requiredParticipants, optionalParticipants], participant) => {
				if (participant.isOptional) return [requiredParticipants, [...optionalParticipants, participant.email]];
				else return [[...requiredParticipants, participant.email], optionalParticipants];
			},
			[new Array<string>(), new Array<string>()],
		);

		let extraEditParams = {};
		if (isEdit) {
			let editType: EventEditType = "THIS";
			if (eventAdditional.recurrenceRule && repeatState !== "NEVER") {
				const selectedEditType = await recurrenceChoiceModal(hasRecurrenceChanges);
				if (!selectedEditType) {
					setFormPreLoading(false);
					return;
				}

				editType = selectedEditType;
			}

			extraEditParams = {
				guid: eventAdditional.guid,
				editType,
			};
		}

		setFormLoading(true);
		const variables = {
			captchaToken,
			calendar: selectedCalendar.id,
			isAllDay: timeState.isAllDay,
			start: userToSystemTime(timeState.start).toISOString(),
			end: userToSystemTime(timeState.end).toISOString(),
			title,
			repeat: repeatState,
			customRrule,
			attendees,
			optionalAttendees,
			conferenceProvider,
			sendNotifications: Boolean(sendNotifications),
			description: eventDescription,
			eventColor,
			...extraEditParams,
			schedulingAutomationIsActive: shouldShowAutoRecordingToggle ? schedulingAutomationIsActive : undefined,
		};
		const eventOrError = await saveEvent(variables);

		dispatchAnalyticsTrack("Event form", {
			feature: "Calendar",
			action: "submit done",
			variables,
			result: eventOrError,
		});

		if ("guid" in eventOrError) {
			onFormFinish({
				type: "succeeded",
				event: {
					start: systemToUserTime(eventOrError.start),
					end: systemToUserTime(eventOrError.end),
					title: eventOrError.title || "",
					isAllDay: Boolean(eventOrError.isAllDay),
					calendarId: eventOrError.calendar,
					eventGuid: eventOrError.guid,
				},
			});

			if (eventOrError.warnings) {
				const warnNode = (
					<div>
						<div>Event saved with warnings:</div>
						{eventOrError.warnings.map(warning => (
							<div key={warning}>{warning}</div>
						))}
					</div>
				);
				toast.warn(warnNode);
			}
		} else {
			toast.error(eventOrError.message);
			onFormFinish({ type: "failed" });
		}

		setFormPreLoading(false);
		setFormLoading(false);
	}, [
		title,
		selectedCalendar,
		timeState,
		repeatState,
		hasRecurrenceChanges,
		customRrule,
		selectedParticipants,
		conferenceProvider,
		saveEvent,
		isEdit,
		onFormFinish,
		eventAdditional,
		eventDescription,
		systemToUserTime,
		userToSystemTime,
		eventColor,
		schedulingAutomationIsActive,
		shouldShowAutoRecordingToggle,
	]);

	const submitFormViaClick = React.useCallback(
		async (e: React.FormEvent<HTMLFormElement>) => {
			e.preventDefault();
			dispatchAnalyticsTrack("Event form", { feature: "Calendar", action: "submit clicked" });

			return submitForm();
		},
		[submitForm],
	);

	const submitFormViaShortcut = React.useCallback(() => {
		dispatchAnalyticsTrack("Keyboard shortcut used: Event form", { feature: "Calendar", action: "submit clicked" });
		return submitForm();
	}, [submitForm]);

	const endOfFormRef = React.useRef<HTMLDivElement>(null);
	const showScrollIndicator = !useInView(endOfFormRef);

	const toggleID = useID();

	const recordToggleRow = (
		<>
			<div className={styles.divider} />
			<div className={styles.formSection}>
				<div className={styles.formSectionRow}>
					<RecordIconSwitch
						checked={schedulingAutomationIsActive}
						onCheckedChange={setSchedulingAutomationIsActive}
						id={toggleID}
						className={styles.recordIconSwitch}
					/>
					<label className={styles.recordToggleText} htmlFor={toggleID}>
						{schedulingAutomationIsActive ? "This meeting will be recorded" : "Enable Auto-record"}
					</label>
				</div>
				{viewer.remainingMeetingRecordingCredits === 0 && <OutOfCreditsWarning withSwitch viewer={viewer} />}
			</div>
		</>
	);

	return (
		<>
			<GlobalKeyboardListener sequence="mod+enter" onEvent={submitFormViaShortcut} />
			<form id="event_form" className={styles.formContent} onSubmit={submitFormViaClick}>
				<div className={styles.formSection}>
					<Input
						name="event_title"
						innerRef={firstInputRef}
						placeholder="Add event title"
						className={cx(styles.titleInput, { [styles.error]: Boolean(titleError) })}
						value={title}
						onChange={onTitleChange}
						onKeyDown={blurOnEscape}
					/>
					{titleError && <small className={styles.inputError}>{titleError}</small>}
				</div>
				<div className={styles.divider} />
				<div className={cx(styles.formSection, styles.timeSection)}>
					<div className={styles.formSectionRow}>
						<FontAwesomeIcon icon={faCalendarDay} className={styles.formSectionRowIcon} />
						{timeState.isAllDay ? (
							<>
								<span className={cx(styles.dateSelectorWidthManager, { [styles.short]: isThisYear(timeState.start) })}>
									<DateInput date={timeState.start} setDate={changeStartDate} labelFormat="short" />
								</span>
								<FontAwesomeIcon icon={faLongArrowRight} className={styles.formSectionRowIcon} />
								<DateInput date={timeState.end} setDate={changeEndDate} labelFormat="short" />
							</>
						) : (
							<DateInput date={timeState.start} setDate={changeStartDate} labelFormat="long" />
						)}
					</div>
					{!timeState.isAllDay && (
						<>
							<div className={styles.formSectionRow}>
								<FontAwesomeIcon icon={faClock} className={styles.formSectionRowIcon} />
								<span className={styles.timeSelectorWidthManager}>
									<TimeSelector time={dateToTimeValue(timeState.start)} setTime={changeStartTime} />
								</span>
								<FontAwesomeIcon icon={faLongArrowRight} className={styles.formSectionRowIcon} />
								<TimeSelector
									time={dateToTimeValue(timeState.end)}
									setTime={changeEndTime}
									timeToCompare={dateToTimeValue(timeState.start)}
								/>
							</div>
							{shouldRenderSpeedyMeetings && meetingGuidelinesSettings && (
								<SpeedyMeetings
									eventStartDate={timeState.start}
									eventEndDate={timeState.end}
									changeTime={changeTime}
									promptBehaviour={meetingGuidelinesSettings.breaksBetweenMeetingsBehaviour}
								/>
							)}
						</>
					)}
					{isExtraOptionsOpen ? (
						<>
							<div className={styles.formSectionRow}>
								<FontAwesomeIcon icon={faArrowsV} className={styles.formSectionRowIcon} />
								<label htmlFor="create-event-panel-all-day-checkbox" className={styles.allDayToggle}>
									<Checkbox
										id="create-event-panel-all-day-checkbox"
										checked={timeState.isAllDay}
										onCheckedChange={toggleAllDay}
									/>
									&ensp;All day
								</label>
							</div>
							<div className={styles.formSectionRow}>
								<FontAwesomeIcon icon={faRepeat} className={styles.formSectionRowIcon} />
								<Selector<RepeatOption>
									blurInputOnSelect
									value={repeatOptions.find(option => option.repeat === repeatState)}
									onChange={onChangeRecurrence}
									options={repeatOptions}
								/>
							</div>
							{timeState.timezone && (
								<div className={styles.formSectionRow}>
									<FontAwesomeIcon className={styles.formSectionRowIcon} icon={faGlobeAmericas} />
									<span className={styles.timezoneLabel}>{timeState.timezone}</span>
								</div>
							)}
						</>
					) : (
						<Button size="slim" variant="tertiary" className={styles.moreOptionsButton} onClick={toggleExtraOptions}>
							<FontAwesomeIcon icon={faEllipsisH} className={styles.formSectionRowIcon} />
							<span>All day&emsp;Repeat&emsp;Time zone</span>
						</Button>
					)}
					{meetingGuidelinesSettings?.noMeetingDaysEnabled && hasAtLeastOneRequiredParticipant && (
						<NoMeetingDay
							eventStartDate={timeState.start}
							setRendered={setNoMeetingDayRendered}
							noMeetingDay={meetingGuidelinesSettings.noMeetingDay}
						/>
					)}
					{meetingGuidelinesSettings?.shortNoticeEnabled &&
						!timeState.isAllDay &&
						hasAtLeastOneRequiredParticipant &&
						!isEdit && (
							<ShortNotice
								eventStartDate={timeState.start}
								setRendered={setShortNoticeRendered}
								hoursInAdvanceThreshold={meetingGuidelinesSettings.shortNoticeNumHours}
							/>
						)}
					{!isEdit &&
						hasAtLeastOneRequiredParticipant &&
						meetingGuidelinesSettings?.finiteRecurringMeetingsEnabled &&
						!(meetingGuidelinesRendered.noMeetingDay && meetingGuidelinesRendered.shortNotice) && (
							<FiniteRecurringMeetings
								repeatState={repeatState}
								customRrule={customRrule}
								attendees={selectedParticipants}
								setRendered={setFiniteRecurringMeetingsRendered}
							/>
						)}
				</div>
				<div className={styles.divider} />
				<div className={styles.formSection}>
					<div className={styles.participantsSection}>
						<ParticipantsSelector
							selectedParticipants={selectedParticipants}
							changeParticipants={changeParticipants}
							viewer={viewer}
							meetingGuidelinesSettings={meetingGuidelinesSettings}
							meetingCostSettings={meetingCostSettings}
							setMeetingAttendeesRendered={setMeetingAttendeesRendered}
						/>
					</div>
				</div>
				<div className={styles.divider} />
				<div className={styles.formSection}>
					<div className={styles.formSectionRow}>
						<FontAwesomeIcon icon={faVideo} className={styles.formSectionRowIcon} />
						<Selector
							blurInputOnSelect
							value={{
								label: CONFERENCE_PROVIDERS[conferenceProvider],
								value: conferenceProvider,
							}}
							options={conferenceOptions}
							onChange={onChangeConferenceProvider}
							formatOptionLabel={conferenceOptionLabelCreator}
						/>
					</div>

					<div className={styles.formSectionRow}>
						<FontAwesomeIcon icon={faCalendarCheck} className={styles.formSectionRowIcon} />
						<Menu open={isCalendarMenuOpen} onOpenChange={setCalendarMenuOpen}>
							<Menu.Trigger>
								<Button
									variant="tertiary"
									size="slim"
									className={cx(styles.calendarSelectorTrigger, { [styles.active]: isCalendarMenuOpen })}
								>
									<CalendarSelectorValueDisplay
										fellowColor={eventColor}
										providerEventColor={eventAdditional?.providerEventColor || null}
										calendarColor={calendarColor}
										selectedCalendar={selectedCalendar}
									/>
								</Button>
							</Menu.Trigger>
							<Menu.Content>
								<Menu.RadioGroup value={selectedCalendar.id}>
									{isEdit ? (
										<CalendarSelectorOption calendar={selectedCalendar} selected={true} onSelect={noop} />
									) : (
										calendars
											.filter(cal => cal.syncEnabled)
											.map(cal => (
												<CalendarSelectorOption
													key={cal.id}
													calendar={cal}
													selected={cal.id === selectedCalendar.id}
													onSelect={changeCalendarFn(cal)}
												/>
											))
									)}
								</Menu.RadioGroup>
								<Menu.Separator />
								<ColorPicker
									calendarColor={calendarColor}
									selectedColor={eventColor}
									providerEventColor={eventAdditional?.providerEventColor || null}
									onSelectColor={onChangeColor}
								/>
							</Menu.Content>
						</Menu>
					</div>
					<div className={cx(styles.formSectionRow, { [styles.textAreaRowOpen]: isDescriptionBoxVisible })}>
						<FontAwesomeIcon icon={faAlignLeft} className={styles.formSectionRowIcon} />
						{isDescriptionBoxVisible ? (
							<TextArea
								className={styles.textArea}
								placeholder="Extra notes that might be useful to attendees."
								resizable
								value={eventDescription}
								onChange={onChangeEventDescription}
							/>
						) : (
							<button className={styles.addDescriptionButton} onClick={toggleDescriptionBox} type="button">
								Add a description
							</button>
						)}
					</div>
				</div>
				{shouldShowAutoRecordingToggle && recordToggleRow}
			</form>

			<div ref={endOfFormRef} style={{ marginTop: 25 }} />

			<footer className={cx(styles.footer, { [styles.shadow]: showScrollIndicator })}>
				{shouldShowSaveAnywayPrompt && (
					<div className={guidelinesPromptStyles.meetingPrompt}>
						<div className={guidelinesPromptStyles.meetingPromptIcon}>
							<FontAwesomeIcon fixedWidth icon={faExclamationTriangle} />
						</div>
						<div>
							<div className={guidelinesPromptStyles.headlineText}>
								This event does not meet{" "}
								{meetingGuidelinesSettings?.promptWhenSavingGuidelinesUrl ? (
									<a
										target="_blank"
										href={meetingGuidelinesSettings?.promptWhenSavingGuidelinesUrl}
										rel="noopener noreferrer"
									>
										your meeting guidelines
									</a>
								) : (
									"your meeting guidelines"
								)}
								.
							</div>
						</div>
					</div>
				)}
				{formLoading ? (
					<span className={styles.footerSpinner}>
						<LoadingSpinner inline dimmer={false} size="small" />
					</span>
				) : (
					<Tooltip disabled={!titleError && !emptyTitleError} placement="top" content={titleError || emptyTitleError}>
						<span>
							<Button
								variant="primary"
								size="slim"
								type="submit"
								form="event_form"
								disabled={Boolean(titleError || emptyTitleError || formPreLoading)}
								className={cx({ [styles.saveAnywayButton]: shouldShowSaveAnywayPrompt })}
							>
								{shouldShowSaveAnywayPrompt ? "Save anyway" : "Save"}
							</Button>
						</span>
					</Tooltip>
				)}
				<Button variant="secondary" size="slim" onClick={onCancel} type="button">
					Cancel
				</Button>
			</footer>
		</>
	);
}
